import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { StepPersonal } from "@/components/onboarding/StepPersonal";
import { StepBrandGrowth } from "@/components/onboarding/StepBrandGrowth";
import { StepTeam } from "@/components/onboarding/StepTeam";
import { StepSocials } from "@/components/onboarding/StepSocials";
import { StepSizes } from "@/components/onboarding/StepSizes";
import { StepPro } from "@/components/onboarding/StepPro";
import { StepPlatforms } from "@/components/onboarding/StepPlatforms";
import { StepBanking } from "@/components/onboarding/StepBanking";
import { StepDocuments } from "@/components/onboarding/StepDocuments";
import { StepReview } from "@/components/onboarding/StepReview";

const DRAFT_KEY = "xyion_onboarding_draft";

const emptyTeamMember = () => ({ name: "", email: "", phone: "" });

const emptyForm = () => ({
  artistName: "",
  brandGrowth: {
    currentState: "", currentAudience: "", strongestPlatforms: "", targetAudience: "",
    brandIdentity: "", upcomingReleases: "", dreamCollabs: "", dreamBrands: "",
    growthMarkets: "", successVision: "",
  },
  personal: {
    fullLegalName: "", companyName: "", ein: "", ssn: "", dob: "",
    mailingAddress: "", dietRestrictions: "", allergies: "",
    emergencyName: "", emergencyPhone: "", emergencyAddress: "",
  },
  team: {
    manager: emptyTeamMember(), coManager: emptyTeamMember(), dayToDay: emptyTeamMember(),
    businessManagement: emptyTeamMember(), legal: emptyTeamMember(), tourManager: emptyTeamMember(),
  },
  socials: { instagram: "", twitter: "", facebook: "", snapchat: "", tiktok: "", youtube: "", other: "" },
  sizes: { top: "", pant: "", waist: "", bust: "", shoe: "" },
  pro: {
    ascap: { username: "", password: "" },
    bmi: { username: "", password: "" },
    sesac: { username: "", password: "" },
    soundexchange: { username: "", password: "" },
    mlc: { username: "", password: "" },
  },
  platforms: {
    spotify: { username: "", password: "" },
    appleMusic: { username: "", password: "" },
  },
  banking: { accountHolder: "", bankName: "", accountType: "", routingNumber: "", accountNumber: "" },
  documents: [],
  signature: "",
  agreed: false,
});

const STEPS = [
  { title: "Brand + Growth", sub: "Vision & direction" },
  { title: "Personal", sub: "Legal & identity" },
  { title: "Team", sub: "Your circle" },
  { title: "Socials", sub: "Digital presence" },
  { title: "Sizes", sub: "Merch & styling" },
  { title: "PRO Logins", sub: "Confidential" },
  { title: "Platform Access", sub: "Artist analytics" },
  { title: "Banking", sub: "Royalty payouts" },
  { title: "Documents", sub: "The vault" },
  { title: "Review", sub: "Sign & submit" },
];

const loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return emptyForm();
    const parsed = JSON.parse(raw);
    const base = emptyForm();
    return {
      ...base, ...parsed,
      brandGrowth: { ...base.brandGrowth, ...(parsed.brandGrowth || {}) },
      personal: { ...base.personal, ...(parsed.personal || {}) },
      socials: { ...base.socials, ...(parsed.socials || {}) },
      sizes: { ...base.sizes, ...(parsed.sizes || {}) },
      team: { ...base.team, ...(parsed.team || {}) },
      pro: { ...base.pro, ...(parsed.pro || {}) },
      platforms: { ...base.platforms, ...(parsed.platforms || {}) },
      banking: { ...base.banking, ...(parsed.banking || {}) },
      documents: Array.isArray(parsed.documents) ? parsed.documents : [],
    };
  } catch {
    return emptyForm();
  }
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1646206346896-14367dee001b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHszfHxyZWNvcmRpbmclMjBzdHVkaW8lMjBuZW9uJTIwZGFyayUyMG1pYyUyMGNvbnNvbGV8ZW58MHx8fHwxNzg5MzY0MTg2fDA&ixlib=rb-4.1.0&q=85";

export default function OnboardingForm() {
  const [form, setForm] = useState(loadDraft);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
  }, [form]);

  const update = (path, value) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const progress = useMemo(() => Math.round((step / (STEPS.length - 1)) * 100), [step]);

  const canContinue = step !== 1 || (form.artistName.trim() && form.personal.fullLegalName.trim());

  const next = () => {
    if (!canContinue) {
      toast.error("Artist name and full legal name are required.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!form.agreed || !form.signature.trim()) {
      toast.error("Please sign and confirm the acknowledgment.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/onboarding`, {
        artist_name: form.artistName.trim(),
        brand_growth: form.brandGrowth,
        personal: form.personal,
        team: form.team,
        socials: form.socials,
        sizes: form.sizes,
        pro_logins: form.pro,
        platforms: form.platforms,
        banking: form.banking,
        documents: form.documents,
        signature: form.signature.trim(),
        agreed: form.agreed,
      });
      localStorage.removeItem(DRAFT_KEY);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(emptyForm());
    setStep(0);
    setSubmitted(false);
  };

  const stepProps = { form, update };

  return (
    <div className="min-h-screen bg-[#08090E]">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <Link
          to="/admin/login"
          data-testid="btn-admin-signin"
          className="absolute top-4 right-4 z-10 inline-flex items-center gap-2 rounded-full border border-white/15 bg-slate-950/60 backdrop-blur px-4 py-2 text-xs font-semibold text-slate-200 transition-colors hover:border-violet-500/50 hover:text-violet-300"
        >
          <Lock size={13} /> Admin Sign In
        </Link>
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08090E]/40 via-[#08090E]/80 to-[#08090E]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-14 pb-10 text-center">
          <p className="xy-eyebrow mb-2">a division of</p>
          <p className="font-display text-sm sm:text-base font-semibold uppercase tracking-[0.35em] text-slate-200">x | a</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-slate-500 mb-3">content + mgmt + tech</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none" data-testid="onboarding-title">
            XYION
          </h1>
          <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-xl mx-auto">
            New client onboarding. Tell us who you are, who's on your team, and how to reach your world —
            we'll handle the rest.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-24">
        {submitted ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="xy-card p-10 md:p-14 text-center"
            data-testid="onboarding-success"
          >
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-emerald-400/40 bg-emerald-400/10 shadow-[0_0_30px_rgba(16,185,129,0.25)]">
              <Check size={28} className="text-emerald-300" />
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
              Welcome to the roster{form.artistName ? `, ${form.artistName}` : ""}.
            </h2>
            <p className="mt-3 text-sm text-slate-400 max-w-md mx-auto">
              Your onboarding package is in. The Xyion team will review everything and reach out if we need
              anything else.
            </p>
            <button
              data-testid="btn-submit-another"
              onClick={reset}
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-slate-200 hover:border-violet-500/50 hover:text-violet-300 transition-colors"
            >
              Submit another artist
            </button>
          </motion.div>
        ) : (
          <>
            {/* Stepper */}
            <div className="mb-8" data-testid="onboarding-stepper">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">
                  Step {step + 1} of {STEPS.length} — <span className="text-violet-300">{STEPS[step].title}</span>
                </p>
                <p className="text-xs font-mono text-slate-500" data-testid="onboarding-progress-label">{progress}%</p>
              </div>
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-violet-600 to-violet-400 shadow-[0_0_12px_rgba(139,92,246,0.6)] transition-all duration-500"
                  style={{ width: `${progress}%` }}
                  data-testid="onboarding-progress-bar"
                />
              </div>
              <div className="hidden md:flex justify-between mt-4">
                {STEPS.map((s, i) => (
                  <div key={s.title} className="flex items-center gap-2">
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-mono transition-colors ${
                        i < step
                          ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-300"
                          : i === step
                            ? "border-violet-400/60 bg-violet-500/20 text-violet-200 shadow-[0_0_12px_rgba(139,92,246,0.4)]"
                            : "border-white/10 text-slate-600"
                      }`}
                    >
                      {i < step ? <Check size={11} /> : i + 1}
                    </span>
                    <span className={`text-[11px] ${i === step ? "text-slate-200" : "text-slate-600"}`}>{s.title}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Step body */}
            <div className="xy-card p-6 md:p-10" data-testid={`onboarding-step-${STEPS[step].title.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="mb-8">
                <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{STEPS[step].title}</h2>
                <p className="text-sm text-slate-500 mt-1">{STEPS[step].sub}</p>
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.25 }}
                >
                  {step === 0 && <StepBrandGrowth {...stepProps} />}
                  {step === 1 && <StepPersonal {...stepProps} />}
                  {step === 2 && <StepTeam {...stepProps} />}
                  {step === 3 && <StepSocials {...stepProps} />}
                  {step === 4 && <StepSizes {...stepProps} />}
                  {step === 5 && <StepPro {...stepProps} />}
                  {step === 6 && <StepPlatforms {...stepProps} />}
                  {step === 7 && <StepBanking {...stepProps} />}
                  {step === 8 && <StepDocuments {...stepProps} />}
                  {step === 9 && <StepReview {...stepProps} />}
                </motion.div>
              </AnimatePresence>

              {/* Nav */}
              <div className="mt-10 flex items-center justify-between border-t border-white/10 pt-6">
                <button
                  data-testid="btn-prev-step"
                  onClick={back}
                  disabled={step === 0}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-300 transition-colors hover:border-white/30 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} /> Back
                </button>
                {step < STEPS.length - 1 ? (
                  <button
                    data-testid="btn-next-step"
                    onClick={next}
                    className="inline-flex items-center gap-2 rounded-full bg-violet-600 px-6 py-2.5 text-sm font-semibold text-white shadow-[0_0_20px_rgba(139,92,246,0.35)] transition-all hover:bg-violet-500 hover:shadow-[0_0_28px_rgba(139,92,246,0.5)]"
                  >
                    Continue <ChevronRight size={16} />
                  </button>
                ) : (
                  <button
                    data-testid="btn-submit-onboarding"
                    onClick={submit}
                    disabled={submitting || !form.agreed || !form.signature.trim()}
                    className="inline-flex items-center gap-2 rounded-full bg-violet-600 px-6 py-2.5 text-sm font-semibold text-white shadow-[0_0_20px_rgba(139,92,246,0.35)] transition-all hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Submitting..." : "Submit Onboarding"} <Check size={16} />
                  </button>
                )}
              </div>
            </div>

            <p className="mt-6 text-center text-xs text-slate-600">
              Your progress is auto-saved on this device. Confidential info (SSN, PRO logins) is stored securely and
              visible only to Xyion HQ.
            </p>
          </>
        )}

        <div className="mt-10 text-center">
          <Link
            to="/admin/login"
            data-testid="link-admin-login"
            className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.2em] text-slate-600 hover:text-violet-300 transition-colors"
          >
            <Lock size={12} /> Xyion HQ
          </Link>
        </div>
      </div>
    </div>
  );
}
